import React from 'react'
import { useFocusStore } from '../stores/focusStore'
import { Timer } from 'lucide-react'
import { useMonitoring } from '../hooks/useMonitoring'
import type { MonitorItem } from '../types-monitor'
import { Button } from '@/shared/ui'
import LoadingCandles from '@/shared/ui/LoadingCandles'

const MONITOR_BATCH = 6

export default function MonitoringList() {
  const { items, loading, error } = useMonitoring()
  return (
    <div className="w-full">
      {/* Desktop: mantém layout original */}
      <div className="hidden lg:block">
        <DesktopMonitoring items={items} loading={loading} error={error as any} />
      </div>
      {/* Mobile: novo layout */}
      <div className="lg:hidden">
        <MobileMonitoring items={items} loading={loading} error={error as any} />
      </div>
    </div>
  )
}

function DesktopMonitoring({
  items,
  loading,
  error,
}: {
  items: MonitorItem[]
  loading: boolean
  error: any
}) {
  return (
    <div className="w-full">
      <div className="mb-3">
        <div className="text-lg font-semibold">Monitorando</div>
        <div className="text-xs text-[#434343]">
          Potenciais identificadas, aguardando confirmar a oportunidade
        </div>
      </div>
      {error && (
        <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
          {String(error)}
        </div>
      )}
      {loading ? (
        <div className="text-sm text-[#434343]">Carregando...</div>
      ) : (items?.length || 0) === 0 ? (
        <div className="text-sm text-[#434343]">Nenhum monitor ativo no momento.</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {items?.map((it) => (
              <DesktopRow key={it.id} item={it} />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function DesktopRow({ item }: { item: MonitorItem }) {
  const focusId = useFocusStore((s)=> s.focusId)
  const highlight = String(focusId || '') === String(item.id)
  return (
    <div id={`opp-card-${item.id}`} className={["rounded-xl border bg-background/40 p-4", highlight ? 'ring-2 ring-lime-400 border-lime-400' : 'border-[#161616]'].join(' ')}>
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          {item.logo_url ? (
            <img src={item.logo_url} alt={item.symbol} className="h-8 w-8 flex-shrink-0 rounded-full object-cover" />
          ) : (<div className="h-8 w-8 flex-shrink-0 rounded-full bg-muted" />)}
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{item.name || item.symbol}</div>
            <div className="truncate text-xs text-[#434343]">{item.symbol}</div>
          </div>
        </div>
        <div>
          <div className="text-sm font-medium">Aguardando</div>
          <div className="text-xs text-[#434343]">Recomendação</div>
        </div>
        <div className="flex items-center gap-2">
          <Timer className="h-5 w-5 text-amber-400 animate-spin-slow" />
        </div>
      </div>
    </div>
  )
}

function MobileMonitoring({
  items,
  loading,
  error,
}: {
  items: MonitorItem[]
  loading: boolean
  error: any
}) {
  const [visibleCount, setVisibleCount] = React.useState(0)
  const [anim, setAnim] = React.useState(false)
  const sentinelRef = React.useRef<HTMLDivElement | null>(null)
  const [autoLoadEnabled, setAutoLoadEnabled] = React.useState(true)

  React.useEffect(() => {
    const total = items?.length || 0
    if (!total) {
      setVisibleCount(0)
      return
    }
    setVisibleCount((current) => {
      if (!current) return Math.min(MONITOR_BATCH, total)
      if (current > total) return total
      return current
    })
  }, [items])

  const visible = Array.isArray(items) && visibleCount
    ? items.slice(0, visibleCount)
    : items ?? []
  const hasMore = (items?.length || 0) > (visible?.length || 0)

  const handleLoadMore = React.useCallback(() => {
    const total = items?.length || 0
    if (!total) return
    setAnim(true)
    setVisibleCount((c) => Math.min(c + MONITOR_BATCH, total))
  }, [items])

  React.useEffect(() => {
    if (!hasMore) return
    const el = sentinelRef.current
    if (!el || typeof IntersectionObserver === 'undefined') {
      setAutoLoadEnabled(false)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            handleLoadMore()
          }
        })
      },
      { rootMargin: '120px 0px 0px 0px' },
    )

    observer.observe(el)

    return () => {
      observer.disconnect()
    }
  }, [handleLoadMore, hasMore])

  return (
    <div className="w-full">
      <div className="mb-3">
        <div className="text-lg font-semibold">Monitorando</div>
      </div>
      {error && (
        <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
          {String(error)}
        </div>
      )}
      {loading ? (
        <div className="text-sm text-[#434343]">Carregando...</div>
      ) : (items?.length || 0) === 0 ? (
        <div className="text-sm text-[#434343]">Nenhum monitor ativo no momento.</div>
      ) : (
        <>
          <div
            className={`grid grid-cols-2 gap-3 transition-all duration-300 ease-out ${
              anim ? 'opacity-90 scale-[0.995]' : 'opacity-100 scale-100'
            }`}
            onTransitionEnd={() => setAnim(false)}
          >
            {visible.map((it) => (
              <MobileRow key={it.id} item={it} />
            ))}
          </div>
          {hasMore &&
            (autoLoadEnabled ? (
              <div
                ref={sentinelRef}
                className="mt-4 flex w-full items-center justify-center py-2"
              >
                <LoadingCandles
                  className="min-h-[80px]"
                  label="Carregando mais monitores..."
                />
              </div>
            ) : (
              <div className="mt-4 flex w-full items-center justify-center">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  className="rounded-md bg-foreground px-4 py-2 text-sm font-medium text-background hover:opacity-90"
                >
                  Ver mais
                </button>
              </div>
            ))}
        </>
      )}
    </div>
  )
}

function MobileRow({ item }: { item: MonitorItem }) {
  const focusId = useFocusStore((s)=> s.focusId)
  const highlight = String(focusId || '') === String(item.id)
  return (
    <div id={`opp-card-${item.id}`} className={["rounded-xl border bg-background/40 p-3", highlight ? 'ring-2 ring-lime-400 border-lime-400' : 'border-[#161616]'].join(' ')}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          {item.logo_url ? (<img src={item.logo_url} alt={item.symbol} className="h-8 w-8 flex-shrink-0 rounded-full object-cover" />) : (<div className="h-8 w-8 flex-shrink-0 rounded-full bg-muted" />)}
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold">{item.name || item.symbol}</div>
            <div className="truncate text-xs text-foreground/60">{item.symbol}</div>
          </div>
        </div>
        <Timer className="h-4 w-4 flex-shrink-0 text-amber-400 animate-spin-slow" />
      </div>
    </div>
  )
}
